import React, { useEffect, useState } from 'react';

import Link from 'next/link';

import XClose from '@/components/svg/XClose';
import MuiTooltipLight from '@/components/V2/Tooltip';
import useEzShip from '@/helpers/hooks/useEzShip';
import useProductFilters from '@/helpers/hooks/useProductFilters';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { generateEzShipURL } from '@/helpers/utils/urlHelpers';

import Headline from './Headline';

type ProductListingHeaderProps = {
  wrapperClassName?: string;
  searchQuery?: string;
  categoryName?: string;
  shouldFetchParentId?: boolean;
} & (
  | {
      isFromStrapi: true;
      totalNumberOfProductsInACategory: number;
      currentCategoryId: string | number;
      currentCategoryName: string;
      currentCategoryProductsCount: number;
      showTotalNumberOfProductsInACategory: boolean;
    }
  | { isFromStrapi?: false }
);

const FetchAndRenderLinkForParentCategory = ({ categoryId }: { categoryId: string | number }) => {
  const [parentCatId, setParentCatId] = useState<string | number>();
  const { ezShipId } = useEzShip();
  const { webalias, generateURL } = useWebAlias();

  useEffect(() => {
    async function fetchParentCatId() {
      try {
        const resp = await fetch('/api/get-parent-category-id-for-deep-categories?categoryId=' + categoryId);
        const data = ((await resp.json()) || {}) as { id: string };
        setParentCatId(data.id);
      } catch (error) {
        console.error('error while fetching parent category id', error);
      }
    }
    fetchParentCatId();
  }, [categoryId]);

  if (!parentCatId) return <></>;

  return (
    <Link href={generateURL(generateEzShipURL(`/products/${encodeURIComponent(parentCatId)}`, ezShipId), webalias)}>
      <XClose width="12" height="12" stroke="#98A2B3" className="v2-cursor-pointer" />
    </Link>
  );
};

const ProductListingHeader = ({
  wrapperClassName,
  searchQuery,
  shouldFetchParentId,
  ...props
}: ProductListingHeaderProps) => {
  const { webalias, generateURL } = useWebAlias();
  const { ezShipId } = useEzShip();
  const {
    totalNumberOfProductsInACategory: totalNumberofProductsInACategoryAlgolia,
    currentCategoryName: currentCategoryNameAlgolia,
    currentCategoryId: currentCategoryIdAlgolia,
    currentCategoryProductsCount: currentCategoryProductsCountAlgolia,
    showTotalNumberOfProductsInACategory: showTotalNumberOfProductsInACategoryAlgolia,
    showFilters,
    firstTwoPets,
    morePets,
    hasMorePets,
    hasPets,
    itemCodes,
  } = useProductFilters();

  const totalNumberOfProductsInACategory = props.isFromStrapi
    ? props.totalNumberOfProductsInACategory
    : totalNumberofProductsInACategoryAlgolia;

  const currentCategoryId = props.isFromStrapi ? props.currentCategoryId : currentCategoryIdAlgolia;

  const currentCategoryName = props.isFromStrapi ? props.currentCategoryName : currentCategoryNameAlgolia;

  const currentCategoryProductsCount = props.isFromStrapi
    ? props.currentCategoryProductsCount
    : currentCategoryProductsCountAlgolia;

  const showTotalNumberOfProductsInACategory = props.isFromStrapi
    ? props.showTotalNumberOfProductsInACategory
    : showTotalNumberOfProductsInACategoryAlgolia;

  return (
    <div className={cn('v2-gap-4  v2-py-2', wrapperClassName)}>
      <Headline className="!v2-text-[30px] v2-text-black !v2-mt-0">
        {currentCategoryName ?? ''}{' '}
        {!searchQuery && (
          <span className="!v2-text-[30px] v2-text-black">
            ({currentCategoryProductsCount ?? 0}
            {showTotalNumberOfProductsInACategory && `/${totalNumberOfProductsInACategory}`})
          </span>
        )}
      </Headline>
      {hasPets && showFilters && itemCodes?.length > 0 && (
        <div className="v2-flex v2-gap-[3px] v2-items-center v2-justify-center v2-border v2-border-[#D0D5DD] v2-bg-white v2-rounded-[6px] py-[2px] px-1">
          <p className="v2-font-sweet-sans-pro v2-text-gray-700 v2-capitalize v2-text-center v2-text-[14px] v2-leading-5 v2-flex v2-items-center v2-gap-1  font-normal">
            <p title={firstTwoPets} className="v2-max-w-[250px] overflow-hidden overflow-ellipsis whitespace-nowrap">
              Showing results for {firstTwoPets}{' '}
            </p>

            {hasMorePets && (
              <p className="v2-flex v2-gap-1">
                <span className="v2-lowercase"> and </span>
                <MuiTooltipLight
                  variant="light"
                  title={
                    <p className="v2-text-[12px] v2-capitalize v2-text-center v2-leading-[18px] v2-font-sweet-sans-pro v2-text-gray-700 font-normal">
                      {morePets?.join(', ')}
                    </p>
                  }
                  placement="top"
                >
                  <span className="v2-underline v2-cursor-pointer">{morePets?.length} More</span>
                </MuiTooltipLight>
              </p>
            )}
          </p>
          <Link
            href={generateURL(
              generateEzShipURL(`/products/${encodeURIComponent(currentCategoryId)}`, ezShipId),
              webalias
            )}
          >
            <XClose width="12" height="12" stroke="#98A2B3" className="v2-cursor-pointer" />
          </Link>
        </div>
      )}

      {!!searchQuery && (
        <div className="v2-flex v2-gap-[3px] v2-items-center v2-justify-center v2-border v2-border-[#D0D5DD] v2-bg-white v2-rounded-[6px] py-[2px] px-1">
          <p className="v2-font-sweet-sans-pro v2-text-gray-700 v2-capitalize v2-text-center v2-text-[14px] v2-leading-5 v2-flex v2-items-center v2-gap-1  font-normal">
            <p title={searchQuery} className="v2-max-w-[250px] overflow-hidden overflow-ellipsis whitespace-nowrap">
              {currentCategoryProductsCount} results for {searchQuery}{' '}
            </p>
          </p>
          {shouldFetchParentId ? (
            <>
              <FetchAndRenderLinkForParentCategory categoryId={currentCategoryId} />
            </>
          ) : (
            <Link
              href={generateURL(
                generateEzShipURL(`/products/${encodeURIComponent(currentCategoryId)}`, ezShipId),
                webalias
              )}
            >
              <XClose width="12" height="12" stroke="#98A2B3" className="v2-cursor-pointer" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductListingHeader;
